import { GUEST_FIRE_MODES } from "@order-up/models";
import { z } from "zod";

/**
 * Zod schema for guest ordering settings — mirroring
 * packages/models/src/guest_ordering/GuestOrderingSettings.ts. Vocabulary is
 * built from the model tuple; nothing is redeclared.
 *
 * Every field is optional (an absent restaurant document resolves through
 * `resolveGuestOrderingSettings`, not here), and the object is `.strict()` so
 * it composes safely inside the strict `RestaurantSettingsSchema`.
 */

export const GuestFireModeSchema = z.enum(GUEST_FIRE_MODES);

/** Minimum guest-facing tab hold, in cents. */
export const GUEST_ORDERING_TAB_HOLD_CENTS_MIN = 500;
/** Maximum guest-facing tab hold, in cents. */
export const GUEST_ORDERING_TAB_HOLD_CENTS_MAX = 100000;
/** Minimum minutes before an untouched tab auto-closes. */
export const GUEST_ORDERING_TAB_AUTO_CLOSE_MINUTES_MIN = 30;
/** Maximum minutes before an untouched tab auto-closes. */
export const GUEST_ORDERING_TAB_AUTO_CLOSE_MINUTES_MAX = 720;

export const GuestOrderingSettingsSchema = z
  .object({
    tablePage: z.boolean().optional(),
    serviceRequests: z.boolean().optional(),
    payAtTable: z.boolean().optional(),
    payFirst: z.boolean().optional(),
    tabs: z.boolean().optional(),
    fireMode: GuestFireModeSchema.optional(),
    tabHoldCents: z
      .number()
      .int()
      .min(GUEST_ORDERING_TAB_HOLD_CENTS_MIN)
      .max(GUEST_ORDERING_TAB_HOLD_CENTS_MAX)
      .optional(),
    tabAutoCloseMinutes: z
      .number()
      .int()
      .min(GUEST_ORDERING_TAB_AUTO_CLOSE_MINUTES_MIN)
      .max(GUEST_ORDERING_TAB_AUTO_CLOSE_MINUTES_MAX)
      .optional(),
  })
  .strict();

export type GuestOrderingSettingsType = z.infer<typeof GuestOrderingSettingsSchema>;
